import moment from 'moment-with-locales-es6';
import eventBus from '/src/utils/event-bus';
import { KEYS, CONFIG, EVENTS } from '/src/utils/constants';

class StoreService {
    constructor() {
        this.cache = {
        };
    }

    // Funzione per recuperare l'utente attualmente loggato
    getUser() {
        return localStorage.getObject('utente');
    }

    async login(username, password) {
        try {
            const res = await fetch(CONFIG.API_URL + '/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: username,
                    password: password
                })
            });

            const data = await res.json();

            if (!res.ok) {
                console.error('❌ Login fallita:', data.error);
                return;
            }
            localStorage.setObject('token', data.token);
            localStorage.setObject('utente', data.utente);
            return data;
        } catch (err) {
            console.error('❌ Errore di rete o fetch:', err);
        }
    }

    async logout() {
        localStorage.setObject('token', null);
        localStorage.setObject('utente', null);
        return {
            logout: true
        }
    }

    async fetchComboboxData( { storageKey, storeMethod, formatFn }) {
        const cached = sessionStorage.getItem(storageKey);
        if (cached) {
            try {
                return JSON.parse(cached);
            } catch (e) {
                console.warn(`Errore parsing cache ${storageKey}`, e);
            }
        }

        const data = await storeMethod();
        if (!data?.items)
            return [];

        const formatted = data.items.map(formatFn);
        sessionStorage.setItem(storageKey, JSON.stringify(formatted));
        return formatted;
    }

    async fetchCommesse(sortColumn, sortDirection, page = 1, search_text = '', where = {}) {
        try {
            const limit = CONFIG.N_ITEMS
            const offset = (page - 1) * limit
            const params = new URLSearchParams()
            params.set('limit', limit)
            params.set('offset', offset)

            if (sortColumn)
                params.set('sortField', sortColumn)
            if (sortDirection)
                params.set('sortDir', sortDirection)
            if (search_text)
                params.set('search', search_text)

            for (const [key, value] of Object.entries(where)) {
                params.set(`filter[${key}]`, value)
            }

            const url = `${CONFIG.API_URL}/commesse?${params.toString()}`
            const res = await this.fetchAuth(url)

            if (!res.ok)
                throw new Error('Errore nel recupero delle commesse')

            const data = await res.json()
            return data
        } catch (e) {
            console.error(`Error in fetchCommesse: ${e.message}`, e);
            return {
                items: null,
                code: e.code,
                error: e.message
            }
    }
    }

    async fetchCommessa(id_commessa) {
        try {
            const url = `${CONFIG.API_URL}/commesse/${id_commessa}`
            const res = await this.fetchAuth(url)

            if (!res.ok)
                throw new Error('Errore nel recupero della commessa')

            const data = await res.json()
            return data
        } catch (e) {
            console.error(`Error in fetchCommessa: ${e.message}`, e);
            return {
                items: null,
                code: e.code,
                error: e.message
            }
        }
    }

    async fetchTask(sortColumn, sortDirection, page = 1, search_text = '', where = {}) {
        try {
            const limit = CONFIG.N_ITEMS
            const offset = (page - 1) * limit
            const params = new URLSearchParams()
            params.set('limit', limit)
            params.set('offset', offset)

            if (sortColumn)
                params.set('sortField', sortColumn)
            if (sortDirection)
                params.set('sortDir', sortDirection)
            if (search_text)
                params.set('search', search_text)

            for (const [key, value] of Object.entries(where)) {
                params.set(`filter[${key}]`, value)
            }

            const url = `${CONFIG.API_URL}/task?${params.toString()}`
            const res = await this.fetchAuth(url)

            if (!res.ok)
                throw new Error('Errore nel recupero dei task')

            const data = await res.json()
            return data
        } catch (e) {
            console.error(`Error in fetchTask: ${e.message}`, e);
            return {
                items: null,
                code: e.code,
                error: e.message
            }
    }
    }

    async fetchOre(sortColumn, sortDirection, page = 1, search_text = '', where = {}) {
        try {
            const limit = CONFIG.N_ITEMS
            const offset = (page - 1) * limit
            const params = new URLSearchParams()
            params.set('limit', limit)
            params.set('offset', offset)

            if (sortColumn)
                params.set('sortField', sortColumn)
            if (sortDirection)
                params.set('sortDir', sortDirection)
            if (search_text)
                params.set('search', search_text)

            for (const [key, value] of Object.entries(where)) {
                params.set(`filter[${key}]`, value)
            }

            const url = `${CONFIG.API_URL}/ore?${params.toString()}`
            const res = await this.fetchAuth(url)

            if (!res.ok)
                throw new Error('Errore nel recupero delle ore')

            const data = await res.json()
            return data
        } catch (e) {
            console.error(`Error in fetchOre: ${e.message}`, e);
            return {
                items: null,
                code: e.code,
                error: e.message
            }
    }
    }

    async fetchRisorse(sortColumn = '', sortDirection = 'asc', page = 0, search_text = '') {
        try {
            const params = new URLSearchParams()
            if (page > 0) {
                const limit = CONFIG.N_ITEMS
                const offset = (page - 1) * limit
                params.set('limit', limit)
                params.set('offset', offset)
            }

            if (sortColumn) {
                params.set('sortField', sortColumn)
            } else {
                params.set('sortField', 'cognome')
            }
            if (sortDirection)
                params.set('sortDir', sortDirection)
            if (search_text)
                params.set('search', search_text)

            const url = `${CONFIG.API_URL}/risorse?${params.toString()}`
            const res = await this.fetchAuth(url)

            if (!res.ok)
                throw new Error('Errore nel recupero delle risorse')

            const data = await res.json()
            return data
        } catch (e) {
            console.error(`Error in fetchRisorse: ${e.message}`, e);
            return {
                items: null,
                code: e.code,
                error: e.message
            }
    }
    }

    async fetchStati() {
        try {
            const url = `${CONFIG.API_URL}/stati`
            const res = await this.fetchAuth(url)

            if (!res.ok)
                throw new Error('Errore nel recupero degli stati')

            const data = await res.json()
            return data
        } catch (e) {
            console.error(`Error in fetchStati: ${e.message}`, e);
            return {
                items: null,
                code: e.code,
                error: e.message
            }
        }
    }

    async fetchStatiTask() {
        try {
            const url = `${CONFIG.API_URL}/statitask`
            const res = await this.fetchAuth(url)

            if (!res.ok)
                throw new Error('Errore nel recupero degli stati dei task')

            const data = await res.json()
            return data
        } catch (e) {
            console.error(`Error in fetchStatiTask: ${e.message}`, e);
            return {
                items: null,
                code: e.code,
                error: e.message
            }
        }
    }

    async fetchSocieta() {
        try {
            const url = `${CONFIG.API_URL}/societa`
            const res = await this.fetchAuth(url)

            if (!res.ok)
                throw new Error('Errore nel recupero delle società')

            const data = await res.json()
            return data
        } catch (e) {
            console.error(`Error in fetchSocieta: ${e.message}`, e);
            return {
                items: null,
                code: e.code,
                error: e.message
            }
        }
    }

    async saveTask(id, data) {
        try {
            const url = `${CONFIG.API_URL}/task/${id}`

            const res = await this.fetchAuth(url, {
                method: 'PATCH',
                body: JSON.stringify(data)
            })

            if (!res.ok) {
                throw new Error('Errore aggiornamento task')
            }
            return {
                success: true
            }
        } catch (e) {
            console.error(`Error in saveTask: ${e.message}`, e)
            return {
                success: false,
                error: e.message
            }
        }
    }

    async saveOra(id, data) {
        try {
            let url = `${CONFIG.API_URL}/ore`
            let method = null
            if(id) {
                url += `/${id}`
                method = 'PATCH'
            } else {
                method = 'POST'
            }
            const res = await this.fetchAuth(url, {
                method: method,
                body: JSON.stringify(data)
            })

            if (!res.ok) {
                throw new Error('Errore aggiornamento ore')
            }
            return {
                success: true
            }
        } catch (e) {
            console.error(`Error in saveTask: ${e.message}`, e)
            return {
                success: false,
                error: e.message
            }
        }
    }
    
    async deleteOra(id) {
        try {
            const url = `${CONFIG.API_URL}/ore/${id}`

            const res = await this.fetchAuth(url, {
                method: 'DELETE'
            })

            if (!res.ok) {
                throw new Error('Errore cancellazione ora')
            }
            return {
                success: true
            }
        } catch (e) {
            console.error(`Error in saveTask: ${e.message}`, e)
            return {
                success: false,
                error: e.message
            }
        }
    }

    async fetchAuth(url, options = {}) {
        const token = localStorage.getObject('token')
        if (!token)
            throw new Error('Token mancante')

        const headers = {
            ...options.headers,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }

        const response = await fetch(url, {
            ...options,
            headers
        })

        // Se il token è scaduto o non valido
        if (response.status === 401 || response.status === 403) {
            try {
                const body = await response.json()
                if (body?.error?.includes('Token scaduto') || body?.error?.includes('Token mancante')) {
                    localStorage.removeItem('token')
                    localStorage.removeItem('utente')
                    eventBus.trigger(EVENTS.AUTH.LOGOUT, {reason: 'expired'})
                }
            } catch (e) {
                console.error('Errore durante la lettura dell’errore', e)
            }
        }

        return response
    }
}

export default new StoreService();

Storage.prototype.setObject = function (key, value) {
    this.setItem(key, JSON.stringify(value));
}

Storage.prototype.getObject = function (key) {
    const value = this.getItem(key);
    return value && JSON.parse(value);
}
